import * as React from "react";

// Markdown-lite for weekly updates. The palette is deliberately tiny — the
// subset that survives a future trip to Slack: **bold**, line-start bullets,
// line breaks, and bare URLs. Anything else renders exactly as typed.
// Output is React elements (never innerHTML), so update bodies can't inject
// markup no matter what they contain.

// ---- shared pattern constants -------------------------------------------
const URL_SOURCE = "https?:\\/\\/\\S+";
// Punctuation that ends a sentence around a pasted URL, not the URL itself.
// '*' included so "**https://x.com** rest" doesn't swallow the closing bold
// markers into the href (the markers then render literally — link wins).
const URL_TRAIL_RE = /[.,;:)!?*]+$/;
const BULLET_RE = /^(\s*)([-*])\s+(.*)$/;
// Exported so the composer (drawer.tsx) shares the renderer's idea of what
// a bullet line is — the two must never drift apart.
export const BARE_BULLET_RE = /^\s*[-*]\s*$/;
export const BULLET_PREFIX_RE = /^(\s*[-*] )/;

// Canonical section headers (Progress / Challenges / Next week) — a line that
// IS one of these (optionally with a colon, and content after it on the line).
const CANON_HEADER_RE = /^\s*\*\*\s*(progress|challenges|next week)\s*:?\s*\*\*\s*(.*)$/i;
// A line whose leading bold run is a canonical header — the ONLY thing that
// ends a Progress section. Plain inline bold ("shipped **API v2**") is
// content, not a boundary.
const SECTION_BREAK_RE = /^\s*\*\*\s*(progress|challenges|next week)\b[^*]*\*\*/i;
// \b keeps "Progressive rollout" from being mistaken for the Progress header.
const PROGRESS_HEADER_RE = /^\s*\*\*\s*progress\b[^*]*\*\*/i;
const INLINE_SECTION_RE = /\*\*\s*(progress|challenges|next week)\b[^*]*\*\*/i;

// Long pasted URLs (Jira, Notion) wrap into soup — show them without the
// protocol and middle-ellipsized; the full URL stays in href/title.
const URL_DISPLAY_MAX = 40;
const URL_HEAD = 24;
const URL_TAIL = 12;

type InlineOpts = { links?: boolean };

function displayUrl(url: string): string {
  const bare = url.replace(/^https?:\/\//i, "");
  // Slice by code points, not UTF-16 units — decoded IDN/unicode paths
  // (Notion slugs, CJK, emoji) must not split a surrogate pair into U+FFFD.
  const cps = Array.from(bare);
  return cps.length > URL_DISPLAY_MAX
    ? `${cps.slice(0, URL_HEAD).join("")}…${cps.slice(-URL_TAIL).join("")}`
    : bare;
}

// **bold** pairs, matched left to right; an unpaired ** renders literally.
function renderBold(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let rest = text;
  let n = 0;
  for (;;) {
    const open = rest.indexOf("**");
    if (open === -1) break;
    const close = rest.indexOf("**", open + 2);
    if (close === -1) break; // unpaired — leave the tail as typed
    if (rest.slice(0, open)) out.push(rest.slice(0, open));
    const inner = rest.slice(open + 2, close);
    if (inner) out.push(<strong key={`${keyBase}-b${n++}`}>{inner}</strong>);
    rest = rest.slice(close + 2);
  }
  if (rest) out.push(rest);
  return out;
}

/** Inline formatting for one line of text: URLs first (no formatting is parsed
 *  inside a URL), then bold within the plain segments. */
export function renderInline(text: string, opts: InlineOpts = {}): React.ReactNode {
  const links = opts.links !== false;
  const out: React.ReactNode[] = [];
  const re = new RegExp(URL_SOURCE, "gi"); // fresh instance — no shared lastIndex
  let last = 0;
  let n = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const at = m.index;
    let url = m[0];
    let trail = url.match(URL_TRAIL_RE)?.[0] ?? "";
    url = trail ? url.slice(0, -trail.length) : url;
    // Re-attach closing parens the trail strip consumed when they balance an
    // opener inside the URL (Wikipedia-style /React_(software) links).
    while (
      trail.startsWith(")") &&
      url.split("(").length > url.split(")").length
    ) {
      url += ")";
      trail = trail.slice(1);
    }
    if (at > last) out.push(...renderBold(text.slice(last, at), `t${n}`));
    const shown = displayUrl(url);
    if (!shown) {
      // "https://" with nothing after it — not a real link, keep as typed.
      out.push(url);
    } else if (links) {
      out.push(
        <a key={`u${n}`} href={url} target="_blank" rel="noreferrer" title={url}>
          {shown}
        </a>
      );
    } else {
      out.push(shown);
    }
    if (trail) out.push(trail);
    last = at + m[0].length;
    n++;
  }
  if (last < text.length) out.push(...renderBold(text.slice(last), `t${n}`));
  return out;
}

/** Full block-level renderer for an update body: consecutive `- ` / `* ` lines
 *  group into a list, bare markers (unfilled template bullets) are dropped,
 *  blank lines separate paragraphs, other lines break with <br/>. */
export function renderUpdateBody(body: string): React.ReactNode {
  const lines = body.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];
  let texts: string[] = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul${blocks.length}`}>
        {bullets.map((b, i) => (
          <li key={i}>{renderInline(b)}</li>
        ))}
      </ul>
    );
    bullets = [];
  };
  const flushTexts = () => {
    if (!texts.length) return;
    blocks.push(
      <p key={`p${blocks.length}`}>
        {texts.map((t, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {renderInline(t)}
          </React.Fragment>
        ))}
      </p>
    );
    texts = [];
  };

  for (const line of lines) {
    if (BARE_BULLET_RE.test(line)) continue; // unfilled template bullet
    const b = line.match(BULLET_RE);
    const h = line.match(CANON_HEADER_RE);
    if (h) {
      // Canonical section header — rendered in the drawer's tiny-cap label
      // idiom (same family as TEAM / POD / UPDATES) instead of bold serif.
      // Also normalizes typed case ("Next Week" vs "next week") visually.
      flushBullets();
      flushTexts();
      blocks.push(
        // role=heading: the visual distinction is pure CSS — assistive tech
        // needs real structure (level 5: the drawer's "Updates" is an h4).
        <div className="ut-h" role="heading" aria-level={5} key={`h${blocks.length}`}>
          {h[1]}
        </div>
      );
      if (h[2].trim()) texts.push(h[2].trim());
    } else if (b) {
      flushTexts();
      bullets.push(b[3]);
    } else if (!line.trim()) {
      flushBullets();
      flushTexts();
    } else {
      flushBullets();
      texts.push(line);
    }
  }
  flushBullets();
  flushTexts();
  return blocks;
}

// One line's real content: header run and bullet marker stripped; a bare
// marker ("- " — note trim() alone leaves a stray "-") counts as nothing.
function lineContent(l: string): string {
  const rest = l.replace(SECTION_BREAK_RE, "");
  if (BARE_BULLET_RE.test(rest)) return "";
  return rest.trim().replace(/^[-*]\s+/, "").trim();
}

/** True when the body says something beyond the weekly-format scaffold —
 *  canonical header lines and unfilled bullets alone don't count. Gates the
 *  Post button so a stray click on the template can't persist a junk row. */
export function hasRealContent(body: string): boolean {
  return body.split(/\r?\n/).some(l => lineContent(l) !== "");
}

/** The one-line digest Weekly Review shows: the Progress section for updates
 *  that follow the convention, the first line with real content otherwise. */
export function extractUpdateSummary(body: string): string {
  const lines = body.split(/\r?\n/);
  const at = lines.findIndex(l => PROGRESS_HEADER_RE.test(l));
  const parts: string[] = [];
  const push = (raw: string) => {
    if (BARE_BULLET_RE.test(raw)) return; // unfilled template bullet
    const t = raw.trim().replace(/^[-*]\s+/, "").trim();
    if (t) parts.push(t);
  };

  if (at !== -1) {
    // Same-line content after the header, cut at the next canonical section
    // header (single-line updates pack **Challenges** etc. onto one line).
    // Plain inline bold in the content is kept, not treated as a boundary.
    let tail = lines[at].replace(PROGRESS_HEADER_RE, "");
    const brk = tail.match(INLINE_SECTION_RE);
    if (brk && brk.index !== undefined) tail = tail.slice(0, brk.index);
    push(tail);
    for (let i = at + 1; i < lines.length; i++) {
      if (SECTION_BREAK_RE.test(lines[i])) break;
      push(lines[i]);
    }
  }

  if (parts.length) return parts.join(" · ");

  // No Progress section — or it held only unfilled template bullets. Fall
  // back to the first line with real content, headers and markers stripped.
  for (const l of lines) {
    const t = lineContent(l);
    if (t) return t;
  }
  return "";
}
